import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  InlineGrid,
  TextField,
  Button,
  DataTable,
  Text,
  Box,
  SkeletonBodyText,
  EmptyState,
} from '@shopify/polaris';
import reportsApi from '@/services/reports';

const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

function formatCurrency(dollars: number): string {
  return `$${Number(dollars).toFixed(2)}`;
}

interface StatCardProps {
  label: string;
  value: string;
}

function StatCard({ label, value }: StatCardProps) {
  return (
    <Card>
      <BlockStack gap="100">
        <Text as="p" variant="bodyMd" tone="subdued">
          {label}
        </Text>
        <Text as="p" variant="headingLg">
          {value}
        </Text>
      </BlockStack>
    </Card>
  );
}

export default function AgedReceivables() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(today());

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['report', 'aged-receivables', startDate, endDate],
    queryFn: () => reportsApi.getAgedReceivables({ startDate, endDate }),
  });

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      await reportsApi.downloadCsv('/reports/aged-receivables', { startDate, endDate });
    } finally {
      setExporting(false);
    }
  };

  const rows =
    data?.data?.buckets?.map((item) => [
      item.label,
      item.count,
      formatCurrency(item.amount),
    ]) ?? [];

  return (
    <Page
      title="Aged Receivables"
      backAction={{ content: 'Reports', onAction: () => navigate('/reports') }}
    >
      <Card>
        <BlockStack gap="400">
          <InlineStack gap="400" blockAlign="end">
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={setStartDate}
              autoComplete="off"
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={setEndDate}
              autoComplete="off"
            />
            <Box paddingBlockStart="600">
              <InlineStack gap="200">
                <Button onClick={() => refetch()}>Refresh</Button>
                <Button onClick={handleExport} loading={exporting}>
                  Export CSV
                </Button>
              </InlineStack>
            </Box>
          </InlineStack>

          {isLoading ? (
            <SkeletonBodyText lines={8} />
          ) : isError ? (
            <EmptyState heading="Failed to load report" image="">
              <p>There was an error loading the Aged Receivables report. Try refreshing.</p>
            </EmptyState>
          ) : !data ? (
            <EmptyState heading="No data" image="">
              <p>No receivables data found for the selected date range.</p>
            </EmptyState>
          ) : (
            <BlockStack gap="400">
              <InlineGrid columns={2} gap="400">
                <StatCard label="Total Outstanding" value={formatCurrency(data.data.total?.amount ?? 0)} />
                <StatCard label="Total Invoices" value={String(data.data.total?.count ?? 0)} />
              </InlineGrid>

              <DataTable
                columnContentTypes={['text', 'numeric', 'text']}
                headings={['Aging Bucket', 'Invoices', 'Amount']}
                rows={rows}
                footerContent={rows.length === 0 ? 'No records found' : undefined}
              />
            </BlockStack>
          )}
        </BlockStack>
      </Card>
    </Page>
  );
}
